import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Loader2, Send, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge, Button } from '@/components/ui';
import { errorsApi } from '@/lib/api';
import { cn } from '@/lib/cn';
import { useApiError } from '@/lib/i18n/useApiError';
import type { ErrorListItem, ResolutionAction } from '@/types/api';

interface RelatedErrorsProps {
  sourceId: string;
  runId: string;
}

interface QuickAction {
  key: 'override' | 'confirm' | 'escalate';
  action: ResolutionAction;
  decisionType: string;
  icon: typeof CheckCircle2;
  tone: 'success' | 'neutral' | 'warning';
}

const ACTIONS: QuickAction[] = [
  { key: 'override', action: 'fix_and_accept', decisionType: 'halt_override', icon: CheckCircle2, tone: 'success' },
  { key: 'confirm', action: 'cancel', decisionType: 'halt_confirmed', icon: XCircle, tone: 'neutral' },
  { key: 'escalate', action: 'escalate', decisionType: 'skill_escalation', icon: Send, tone: 'warning' },
];

/**
 * Inline error-resolution panel for the supervision drawer.
 *
 * Lists every `error_queue` row attached to this `(source_id, run_id)` and
 * exposes the three quick-actions analysts reach for first: override the
 * halt, confirm it was correct, or escalate to the skill queue. The full
 * fix-and-accept form (with corrected value) still lives in `/studio/errors`.
 */
export function RelatedErrors({ sourceId, runId }: RelatedErrorsProps) {
  const { t } = useTranslation(['studio', 'common']);
  const apiError = useApiError();
  const queryClient = useQueryClient();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['supervision', 'errors', sourceId, runId],
    // Backend supports `source_id` but not `run_id` — filter client-side.
    queryFn: () => errorsApi.list({ source_id: sourceId, page_size: 50 }),
    retry: false,
    staleTime: 15_000,
  });

  const items = (data?.items ?? []).filter((e) => String(e.run_id) === runId);

  const resolveMut = useMutation({
    mutationFn: async (input: { errorId: string; action: QuickAction }) =>
      errorsApi.resolve(input.errorId, {
        action: input.action.action,
        decision_type: input.action.decisionType,
        log_as_training_signal: true,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['supervision', 'errors', sourceId, runId] });
      void queryClient.invalidateQueries({ queryKey: ['errors', 'list'] });
      void queryClient.invalidateQueries({ queryKey: ['supervision', 'runs'] });
    },
  });

  if (isLoading) {
    return (
      <p className="flex items-center gap-2 text-small text-slate-500">
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
        {t('common:status.loading')}
      </p>
    );
  }
  if (isError) {
    return (
      <p className="flex items-center gap-2 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-small text-amber-800">
        <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
        {apiError(error) ?? t('common:status.error')}
      </p>
    );
  }
  if (items.length === 0) {
    return <p className="text-small text-slate-500">{t('supervision.related.empty')}</p>;
  }

  return (
    <ul className="space-y-2">
      {items.map((err) => (
        <ErrorCard
          key={err.error_id}
          err={err}
          onResolve={(action) =>
            resolveMut.mutate({ errorId: err.error_id, action })
          }
          isPending={
            resolveMut.isPending &&
            resolveMut.variables?.errorId === err.error_id
          }
          pendingKey={
            resolveMut.isPending && resolveMut.variables?.errorId === err.error_id
              ? resolveMut.variables.action.key
              : null
          }
          resolveError={
            resolveMut.isError && resolveMut.variables?.errorId === err.error_id
              ? (apiError(resolveMut.error) ?? t('common:status.error'))
              : null
          }
        />
      ))}
    </ul>
  );
}

function ErrorCard({
  err,
  onResolve,
  isPending,
  pendingKey,
  resolveError,
}: {
  err: ErrorListItem;
  onResolve: (action: QuickAction) => void;
  isPending: boolean;
  pendingKey: QuickAction['key'] | null;
  resolveError: string | null;
}) {
  const { t } = useTranslation('studio');
  const isResolved = err.status !== 'open';
  return (
    <li
      className={cn(
        'rounded border border-slate-200 px-3 py-2',
        isResolved && 'bg-slate-50 opacity-70',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={err.error_type === 'H' ? 'error' : 'warning'}>
              {err.error_type}
            </Badge>
            {err.error_subtype && (
              <span className="font-mono text-small text-slate-600">{err.error_subtype}</span>
            )}
            <Badge variant={isResolved ? 'neutral' : 'warning'}>{err.status}</Badge>
          </div>
          {haltDetail(err) && (
            <p className="mt-1 break-all font-mono text-small text-red-700">
              {haltDetail(err)}
            </p>
          )}
        </div>
      </div>

      {!isResolved && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {ACTIONS.map((a) => (
            <Button
              key={a.key}
              type="button"
              size="sm"
              variant={a.tone === 'success' ? 'primary' : 'secondary'}
              onClick={() => onResolve(a)}
              disabled={isPending}
            >
              {isPending && pendingKey === a.key ? (
                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" aria-hidden />
              ) : (
                <a.icon className="mr-1 h-3.5 w-3.5" aria-hidden />
              )}
              {t(`supervision.related.actions.${a.key}`)}
            </Button>
          ))}
        </div>
      )}
      {resolveError && (
        <p className="mt-2 text-small text-red-700">{resolveError}</p>
      )}
    </li>
  );
}

function haltDetail(err: ErrorListItem): string | null {
  const detail = (err.payload as Record<string, unknown>).detail as
    | Record<string, unknown>
    | undefined;
  if (!detail) return null;
  const reason = typeof detail.reason === 'string' ? detail.reason : null;
  const errStr = typeof detail.error === 'string' ? detail.error : null;
  return reason || errStr;
}
